import React, { useState } from "react";
import api from "../services/api";
import { toast } from "sonner";
import { 
  Briefcase, 
  UploadCloud, 
  Link2, 
  CheckCircle2, 
  Building2, 
  ShieldCheck, 
  Sparkles,
  ArrowRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const LONDON_BOROUGHS = [
  "Kensington and Chelsea",
  "Westminster",
  "Islington",
  "Camden",
  "Canary Wharf",
  "Clapham",
  "Marylebone",
  "Mayfair",
  "Richmond upon Thames",
  "Hackney",
  "Southwark"
];

export const Partners = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    agency_name: "Chestertons Prime Lettings",
    contact_name: "Marcus Bennett",
    contact_email: "m.bennett@chestertons-demo.co.uk",
    contact_phone: "+44 20 7352 2883",
    borough_coverage: ["Kensington and Chelsea", "Westminster"],
    portfolio_size: "50+ Properties",
    integration_type: "Feed URL",
    feed_or_links: "https://feeds.chestertons-demo.co.uk/prime-rentals.xml",
    notes: "Prime central London residential portfolio seeking verified demand briefs."
  });

  const toggleBorough = (b) => {
    setFormData((prev) => {
      const exists = prev.borough_coverage.includes(b);
      return {
        ...prev,
        borough_coverage: exists ? prev.borough_coverage.filter((x) => x !== b) : [...prev.borough_coverage, b]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/partner-submissions", formData);
      setSubmitted(true);
      toast.success("Agency partnership submission received by FIND LET operations!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FBFF] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-12">
        
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-[#D0E4FE] text-purple-900 text-xs font-semibold">
            <Briefcase className="w-3.5 h-3.5" />
            <span>Letting Agents & Portfolio Operators</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#091F5C] font-heading">
            Access Pre-Qualified London Tenant Demand
          </h1>
          <p className="text-[#4A5B78] text-sm max-w-xl mx-auto">
            Partner with FIND LET to feed your available property stock directly into our curated concierge matching pool. Zero portal spam, 100% pre-vetted applicants.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="p-6 rounded-2xl bg-white border border-[#E5EBF5] shadow-sm space-y-2">
            <div className="w-10 h-10 rounded-xl bg-[#D0E4FE] text-[#334DAF] flex items-center justify-center font-bold">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-[#091F5C] font-heading text-base">Pre-Screened Budgets</h3>
            <p className="text-xs text-[#4A5B78] leading-relaxed">
              Every tenant brief includes verified income, moving window, and household configuration.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-[#E5EBF5] shadow-sm space-y-2">
            <div className="w-10 h-10 rounded-xl bg-[#D0E4FE] text-[#334DAF] flex items-center justify-center font-bold">
              <Link2 className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-[#091F5C] font-heading text-base">Flexible Integration</h3>
            <p className="text-xs text-[#4A5B78] leading-relaxed">
              Connect via XML/JSON property feeds, batch spreadsheet uploads, or direct portal links.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-white border border-[#E5EBF5] shadow-sm space-y-2">
            <div className="w-10 h-10 rounded-xl bg-[#D0E4FE] text-[#334DAF] flex items-center justify-center font-bold">
              <Sparkles className="w-5 h-5" />
            </div>
            <h3 className="font-bold text-[#091F5C] font-heading text-base">Zero Upfront Listing Fee</h3>
            <p className="text-xs text-[#4A5B78] leading-relaxed">
              Only pay standard concierge introduction upon successful tenancy completion.
            </p>
          </div>
        </div>

        {/* Intake Form */}
        <div className="bg-white rounded-2xl p-6 sm:p-10 border border-[#E5EBF5] shadow-xl">
          {submitted ? (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-[#D0E4FE] text-[#334DAF] flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-[#091F5C] font-heading">
                Agency Application Received
              </h2>
              <p className="text-sm text-[#4A5B78] max-w-md mx-auto">
                Our operations team has logged your feed credentials. A concierge partner director will reach out within 4 business hours.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="px-6 py-2.5 rounded-xl border border-[#D0E4FE] text-[#4A5B78] text-sm font-semibold"
              >
                Submit Additional Feed
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-[#091F5C] font-heading">
                  Property Partner Registration & Feed Ingestion
                </h3>
                <p className="text-xs text-[#4A5B78] mt-1">
                  Tell us about your agency and how you'd like to share available inventory.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Agency / Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.agency_name}
                    onChange={(e) => setFormData({ ...formData, agency_name: e.target.value })}
                    data-testid="input-partner-agency"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    data-testid="input-partner-contact"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Work Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    data-testid="input-partner-email"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Direct Phone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    data-testid="input-partner-phone"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Active Portfolio Size
                  </label>
                  <select
                    value={formData.portfolio_size}
                    onChange={(e) => setFormData({ ...formData, portfolio_size: e.target.value })}
                    data-testid="select-portfolio-size"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  >
                    <option value="1-10 Properties">1-10 Prime Properties</option>
                    <option value="11-50 Properties">11-50 Properties</option>
                    <option value="50+ Properties">50+ Properties (Full Agency)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                    Preferred Integration Format
                  </label>
                  <select
                    value={formData.integration_type}
                    onChange={(e) => setFormData({ ...formData, integration_type: e.target.value })}
                    data-testid="select-integration-type"
                    className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm"
                  >
                    <option value="Feed URL">Live XML / JSON Feed URL</option>
                    <option value="Document Upload / Spreadsheet">Spreadsheet / PDF Inventory</option>
                    <option value="Direct Link List">Direct Link List / Web Ingestion</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                  Feed URL, Spreadsheets Link, or Portal Listing URLs
                </label>
                <input
                  type="text"
                  placeholder="https://feeds.your-agency.co.uk/rentals.xml or Google Sheet link"
                  value={formData.feed_or_links}
                  onChange={(e) => setFormData({ ...formData, feed_or_links: e.target.value })}
                  data-testid="input-feed-links"
                  className="w-full px-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-2">
                  London Borough Coverage
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {LONDON_BOROUGHS.slice(0, 8).map((b) => {
                    const isChecked = formData.borough_coverage.includes(b);
                    return (
                      <button
                        type="button"
                        key={b}
                        onClick={() => toggleBorough(b)}
                        data-testid={`partner-borough-${b.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`p-2.5 rounded-lg border text-xs text-left ${
                          isChecked ? "bg-[#E8F2FE] border-[#D0E4FE] text-purple-950 font-bold" : "border-[#E5EBF5] text-[#4A5B78]"
                        }`}
                      >
                        {b}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="pt-4 flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-partner-app-btn"
                  className="px-8 py-3.5 rounded-xl bg-purple-700 hover:bg-[#091F5C] text-white font-bold text-sm shadow-lg shadow-purple-900/30 transition-all flex items-center space-x-2 disabled:opacity-50"
                >
                  <Briefcase className="w-4 h-4" />
                  <span>{submitting ? "Submitting Application..." : "Submit Agency Partner Application"}</span>
                </button>
              </div>

            </form>
          )}
        </div>

      </div>
    </div>
  );
};
